import React, { useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import Panel from "../Panel";
import {
  Form,
  FormInput,
  FormGroup,
  Grid,
  GridColumn,
  GridRow,
  Divider,
  Dropdown,
  Button,
  Message,
  Segment,
  Label,
} from "semantic-ui-react";
import { useFormik } from "formik";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import postVodafoneOrder from "../../libs/requests/postVodafoneOrder";
import fetchVodafoneAppointmentSlots from "../../libs/requests/fetchVodafoneAppointmentSlots";
import reserveVodafoneAppointment from "../../libs/requests/reserveVodafoneAppointment";

const CARE_LEVEL_OPTIONS = [
  { key: "CA00001", text: "Standard (CA00001)", value: "CA00001" },
  { key: "CA00002", text: "Enhanced (CA00002)", value: "CA00002" },
  { key: "CA00003", text: "Premium (CA00003)", value: "CA00003" },
];

const TIME_SLOT_OPTIONS = [
  { key: "AM", text: "AM (08:00 – 13:00)", value: "AM" },
  { key: "PM", text: "PM (13:00 – 18:00)", value: "PM" },
  { key: "Any", text: "Any time", value: "Any" },
];

const APPOINTMENT_TYPE_OPTIONS = [
  { key: "Standard", text: "Standard", value: "Standard" },
  { key: "Premium", text: "Premium", value: "Premium" },
  { key: "Advanced", text: "Advanced", value: "Advanced" },
];

function formatAddress(address) {
  if (!address) return "";
  return [
    address.subPremiseName,
    address.premiseName,
    address.thoroughfareNumber && address.thoroughfare
      ? `${address.thoroughfareNumber} ${address.thoroughfare}`
      : address.thoroughfare,
    address.locality,
    address.postTown,
    address.postCode,
  ]
    .filter(Boolean)
    .join(", ");
}

export default function VodafoneOrderForm({ storage = sessionStorage }) {
  const history = useHistory();
  const [product, setProduct] = useState(null);
  const [addressRef, setAddressRef] = useState("");
  const [districtCode, setDistrictCode] = useState("");
  const [uprn, setUprn] = useState("");
  const [accessLineId, setAccessLineId] = useState("");
  const [formattedAddress, setFormattedAddress] = useState("");
  const [slots, setSlots] = useState([]);
  const [slotsLoading, setSlotsLoading] = useState(false);
  const [slotsError, setSlotsError] = useState(null);
  const [availableDates, setAvailableDates] = useState([]);
  const [successRef, setSuccessRef] = useState(null);

  useEffect(() => {
    const raw = storage.getItem("selectedProduct");
    if (raw) setProduct(JSON.parse(raw));

    try {
      const cd = JSON.parse(storage.getItem("customerDetails") ?? "{}");
      const sel = cd?.selectedAddress;
      if (sel) {
        setAddressRef(sel.addressReference?.addressReferenceNumber ?? "");
        setDistrictCode(sel.addressReference?.districtCode ?? "");
        setUprn(sel.addressReference?.uprn ?? "");
        setAccessLineId(sel.accessLineId ?? "");
        setFormattedAddress(sel.address ?? "");
      }
    } catch { }
  }, [storage]);

  const needsAppointment = product?.requirements?.some(
    (r) => r?.name?.toLowerCase() === "appointment" && r?.value?.toLowerCase() === "required"
  ) ?? false;

  const isMigrate = (product?.provisionType ?? 0) === 2;

  useEffect(() => {
    if (!product) return;
    const supplierProductCode = product.SupplierProductCode ?? product.supplierProductCode ?? "";
    if (!addressRef && !accessLineId) return;

    setSlotsLoading(true);
    setSlotsError(null);
    fetchVodafoneAppointmentSlots({
      addressReference: addressRef || undefined,
      districtCode: districtCode || undefined,
      accessLineId: accessLineId || undefined,
      productCode: supplierProductCode || undefined,
    })
      .then((data) => {
        const slotList = Array.isArray(data) ? data : [];
        setSlots(slotList);
        const uniqueDates = [...new Set(slotList.map((s) => s.Date))];
        setAvailableDates(uniqueDates.map((d) => new Date(d)));
      })
      .catch(() => setSlotsError("Could not load appointment slots. You can still enter a preferred date manually."))
      .finally(() => setSlotsLoading(false));
  }, [product]);

  const { values, handleChange, handleSubmit, setFieldValue, isValid, errors, isSubmitting } = useFormik({
    enableReinitialize: true,
    initialValues: {
      orderReference: "",
      firstName: "",
      lastName: "",
      email: "",
      phoneNumber: "",
      careLevel: "CA00001",
      preferredActivationDate: null,
      needsAppointment: needsAppointment,
      appointmentDate: null,
      timeSlot: "AM",
      appointmentType: "Standard",
      appointmentNotes: "",
      hazardNotes: "",
      specialArrangementNotes: "",
    },
    validate(vals) {
      const errs = {};
      if (!vals.orderReference.trim()) errs.orderReference = "Order reference is required.";
      else if (vals.orderReference.trim().length < 3) errs.orderReference = "Order reference must be at least 3 characters.";

      if (!vals.firstName.trim()) errs.firstName = "First name is required.";
      if (!vals.lastName.trim()) errs.lastName = "Last name is required.";
      if (!vals.phoneNumber.trim()) errs.phoneNumber = "Contact phone number is required.";

      if (needsAppointment) {
        if (!vals.appointmentDate) errs.appointmentDate = "Appointment date is required.";
      } else {
        if (!vals.preferredActivationDate) errs.preferredActivationDate = "Preferred activation date is required.";
      }

      return errs;
    },
    onSubmit: async (vals, { setSubmitting }) => {
      const fullName    = `${vals.firstName.trim()} ${vals.lastName.trim()}`.trim();
      const supplierCode = product?.SupplierProductCode ?? product?.supplierProductCode ?? product?.productCode;

      // Reserve the appointment slot with Vodafone before placing the order.
      // The returned AppointmentReference is required in the createOrder SOAP.
      let appointmentReference = undefined;
      if (needsAppointment && vals.appointmentDate) {
        const { startTime, endTime } = getSlotTimes(vals.appointmentDate, vals.timeSlot);
        const reserved = await reserveVodafoneAppointment({
          productCode:          supplierCode,
          addressReference:     addressRef    || undefined,
          accessLineId:         accessLineId  || undefined,
          districtCode:         districtCode  || undefined,
          appointmentDate:      vals.appointmentDate.toISOString().split("T")[0],
          appointmentStartTime: startTime,
          appointmentEndTime:   endTime,
        });
        appointmentReference = reserved?.appointmentReference;
      }

      const payload = {
        OrderReference: vals.orderReference.trim(),
        ProductCode:    supplierCode,
        FeatureCode:    product?.SupplierFeatureCode ?? product?.supplierFeatureCode,
        CareLevel:      vals.careLevel || undefined,
        OrderType:      isMigrate ? "Migrate" : "Provide",
        AddressReference: addressRef  || undefined,
        DistrictCode:     districtCode || undefined,
        Uprn:             uprn         || undefined,
        AccessLineId:     accessLineId || undefined,
        PreferredActivationDate: needsAppointment
          ? vals.appointmentDate?.toISOString().split("T")[0]
          : vals.preferredActivationDate?.toISOString().split("T")[0],
        EndUserContact: {
          FullName:     fullName               || undefined,
          PhoneNumber:  vals.phoneNumber.trim() || undefined,
          Email:        vals.email.trim()       || undefined,
        },
        Appointment: needsAppointment
          ? {
              AppointmentReference:       appointmentReference || undefined,
              AppointmentType:            vals.appointmentType,
              PreferredDate:              vals.appointmentDate?.toISOString().split("T")[0],
              TimeSlot:                   vals.timeSlot,
              Notes:                      vals.appointmentNotes          || undefined,
              HazardNotes:                vals.hazardNotes               || undefined,
              SpecialArrangementNotes:    vals.specialArrangementNotes   || undefined,
            }
          : undefined,
      };

      const result = await postVodafoneOrder(JSON.stringify(payload));
      setSuccessRef(result.reference);
    },
  });

  if (!product) {
    return (
      <Panel header="Place Vodafone Order">
        <Message warning content="No product selected. Please return to Availability and select a product." />
        <Button onClick={() => history.push("/Availability")}>Back to Availability</Button>
      </Panel>
    );
  }

  if (successRef) {
    return (
      <Panel header="Order Submitted">
        <Message positive>
          <Message.Header>Order submitted — awaiting approval</Message.Header>
          <p>Your order reference <strong>{successRef}</strong> has been saved and will appear in Pending Orders for approval. A Vodafone reference will be assigned once it is released.</p>
        </Message>
        <Button className="button-primary" onClick={() => history.push("/")}>
          Back to Home
        </Button>
      </Panel>
    );
  }

  const slotTimesForDate = (date) => {
    if (!date) return [];
    const dateStr = date.toISOString().split("T")[0];
    const seen = new Set();
    return slots
      .filter((s) => s.Date === dateStr)
      .filter((s) => { if (seen.has(s.TimeSlot)) return false; seen.add(s.TimeSlot); return true; })
      .map((s) => ({
        key: s.TimeSlot,
        text: s.TimeSlot === "AM" ? "AM (08:00–13:00)" : s.TimeSlot === "PM" ? "PM (13:00–18:00)" : `${s.StartTime ?? ""} – ${s.EndTime ?? ""}`,
        value: s.TimeSlot,
      }));
  };

  // Returns the widest start/end times for the selected date + time slot (used in reserveEngineerVisit).
  // Falls back to standard window times when no real slot data is available.
  const getSlotTimes = (date, timeSlot) => {
    if (!date || !timeSlot || timeSlot === "Any") {
      return { startTime: "08:00:00", endTime: "18:00:00" };
    }
    const dateStr = date.toISOString().split("T")[0];
    const matching = slots.filter((s) => s.Date === dateStr && s.TimeSlot === timeSlot);
    if (matching.length === 0) {
      if (timeSlot === "AM") return { startTime: "08:00:00", endTime: "13:00:00" };
      if (timeSlot === "PM") return { startTime: "13:00:00", endTime: "18:00:00" };
      return { startTime: "08:00:00", endTime: "18:00:00" };
    }
    const startTime = matching.reduce((min, s) => (s.StartTime < min ? s.StartTime : min), matching[0].StartTime);
    const endTime   = matching.reduce((max, s) => (s.EndTime   > max ? s.EndTime   : max), matching[0].EndTime);
    return { startTime, endTime };
  };

  const isDateAvailable = (date) =>
    availableDates.length === 0 ||
    availableDates.some(
      (d) => d.toISOString().split("T")[0] === date.toISOString().split("T")[0]
    );

  const productLabel = product?.productName ?? product?.productCode ?? "";
  const panelHeader = `Place Vodafone Order — ${productLabel}`;

  return (
    <Panel header={panelHeader}>
      {successRef === null && (
        <Form onSubmit={handleSubmit}>
          {/* Read-only product summary */}
          <Segment secondary>
            <FormGroup>
              <FormInput
                label="Product"
                value={productLabel}
                disabled
                style={{ width: "300px" }}
              />
              <FormInput
                label="Order type"
                value={isMigrate ? "Migrate" : "Provide"}
                disabled
                style={{ width: "150px" }}
              />
            </FormGroup>
            {(product?.SupplierProductCode ?? product?.supplierProductCode) && (
              <FormGroup>
                <FormInput
                  label="Supplier product code"
                  value={product.SupplierProductCode ?? product.supplierProductCode}
                  disabled
                  style={{ width: "180px" }}
                />
                {(product?.SupplierFeatureCode ?? product?.supplierFeatureCode) && (
                  <FormInput
                    label="Feature code (speed)"
                    value={product.SupplierFeatureCode ?? product.supplierFeatureCode}
                    disabled
                    style={{ width: "180px" }}
                  />
                )}
              </FormGroup>
            )}
          </Segment>

          {/* Read-only address */}
          <Segment secondary>
            <strong>Installation address</strong>
            <p style={{ margin: "4px 0 0" }}>
              {formattedAddress || [addressRef, districtCode].filter(Boolean).join(" / ") || "—"}
            </p>
            {uprn && <p style={{ margin: 0, fontSize: "0.85em", color: "#666" }}>UPRN: {uprn}</p>}
            {accessLineId && <p style={{ margin: 0, fontSize: "0.85em", color: "#666" }}>Access line: {accessLineId}</p>}
          </Segment>

          <Divider />

          {/* Order reference */}
          <FormGroup>
            <FormInput
              label="Your order reference *"
              name="orderReference"
              placeholder="e.g. NIMANS-20260716-001"
              value={values.orderReference}
              onChange={handleChange}
              error={errors.orderReference}
              style={{ width: "350px" }}
              required
            />
          </FormGroup>

          <Divider horizontal>Care level</Divider>

          <FormGroup>
            <Form.Field style={{ width: "300px" }}>
              <label>Care level *</label>
              <Dropdown
                selection
                options={CARE_LEVEL_OPTIONS}
                value={values.careLevel}
                onChange={(_, { value }) => setFieldValue("careLevel", value)}
              />
            </Form.Field>
          </FormGroup>

          <Divider horizontal>End user contact</Divider>

          <Grid columns={2}>
            <GridRow>
              <GridColumn>
                <FormInput
                  label="First name *"
                  name="firstName"
                  placeholder="Jane"
                  value={values.firstName}
                  onChange={handleChange}
                  error={errors.firstName}
                  required
                />
              </GridColumn>
              <GridColumn>
                <FormInput
                  label="Last name *"
                  name="lastName"
                  placeholder="Smith"
                  value={values.lastName}
                  onChange={handleChange}
                  error={errors.lastName}
                  required
                />
              </GridColumn>
            </GridRow>
            <GridRow>
              <GridColumn>
                <FormInput
                  label="Contact phone *"
                  name="phoneNumber"
                  placeholder="07700900000"
                  value={values.phoneNumber}
                  onChange={handleChange}
                  error={errors.phoneNumber}
                  required
                />
              </GridColumn>
              <GridColumn>
                <FormInput
                  label="Email address"
                  name="email"
                  type="email"
                  placeholder="jane.smith@example.com"
                  value={values.email}
                  onChange={handleChange}
                />
              </GridColumn>
            </GridRow>
          </Grid>

          {/* Appointment section */}
          {needsAppointment ? (
            <>
              <Divider horizontal>Engineer appointment</Divider>

              {slotsLoading && <Message info content="Loading available appointment slots…" />}
              {slotsError && <Message warning content={slotsError} />}
              {!slotsLoading && slots.length > 0 && (
                <Message info size="small">
                  <Label color="red" size="small">
                    {availableDates.length} available dates
                  </Label>{" "}
                  Only available dates are selectable below.
                </Message>
              )}

              <Grid columns={2}>
                <GridRow>
                  <GridColumn>
                    <Form.Field required error={!!errors.appointmentDate}>
                      <label>Appointment date *</label>
                      <DatePicker
                        selected={values.appointmentDate}
                        onChange={(d) => {
                          setFieldValue("appointmentDate", d);
                          const available = slotTimesForDate(d);
                          if (available.length > 0) setFieldValue("timeSlot", available[0].value);
                        }}
                        filterDate={isDateAvailable}
                        minDate={new Date()}
                        dateFormat="dd/MM/yyyy"
                        placeholderText="Select appointment date"
                      />
                      {errors.appointmentDate && (
                        <Label basic color="red" pointing>
                          {errors.appointmentDate}
                        </Label>
                      )}
                    </Form.Field>
                  </GridColumn>
                  <GridColumn>
                    <Form.Field>
                      <label>Time slot</label>
                      <Dropdown
                        selection
                        options={
                          values.appointmentDate
                            ? slotTimesForDate(values.appointmentDate).length > 0
                              ? slotTimesForDate(values.appointmentDate)
                              : TIME_SLOT_OPTIONS
                            : TIME_SLOT_OPTIONS
                        }
                        value={values.timeSlot}
                        onChange={(_, { value }) => setFieldValue("timeSlot", value)}
                      />
                    </Form.Field>
                  </GridColumn>
                </GridRow>
                <GridRow>
                  <GridColumn>
                    <Form.Field>
                      <label>Appointment type</label>
                      <Dropdown
                        selection
                        options={APPOINTMENT_TYPE_OPTIONS}
                        value={values.appointmentType}
                        onChange={(_, { value }) => setFieldValue("appointmentType", value)}
                      />
                    </Form.Field>
                  </GridColumn>
                </GridRow>
              </Grid>

              <FormGroup>
                <Form.TextArea
                  label="Notes for engineer"
                  name="appointmentNotes"
                  placeholder="Access instructions, parking, entry codes, etc."
                  value={values.appointmentNotes}
                  onChange={handleChange}
                  style={{ width: "100%" }}
                />
              </FormGroup>
              <FormGroup>
                <Form.TextArea
                  label="Hazard notes"
                  name="hazardNotes"
                  placeholder="Any health & safety hazards at the site"
                  value={values.hazardNotes}
                  onChange={handleChange}
                  style={{ width: "100%" }}
                />
              </FormGroup>
              <FormGroup>
                <Form.TextArea
                  label="Special arrangement notes"
                  name="specialArrangementNotes"
                  placeholder="Any special access or booking arrangements"
                  value={values.specialArrangementNotes}
                  onChange={handleChange}
                  style={{ width: "100%" }}
                />
              </FormGroup>
            </>
          ) : (
            <>
              <Divider horizontal>Activation</Divider>
              <FormGroup>
                <Form.Field required error={!!errors.preferredActivationDate}>
                  <label>Preferred activation date *</label>
                  <DatePicker
                    selected={values.preferredActivationDate}
                    onChange={(d) => setFieldValue("preferredActivationDate", d)}
                    minDate={new Date()}
                    dateFormat="dd/MM/yyyy"
                    placeholderText="Select a date"
                  />
                  {errors.preferredActivationDate && (
                    <Label basic color="red" pointing>
                      {errors.preferredActivationDate}
                    </Label>
                  )}
                </Form.Field>
              </FormGroup>
            </>
          )}

          <Divider />

          <Button.Group>
            <Button type="button" onClick={() => history.push("/Availability")} disabled={isSubmitting}>
              Back
            </Button>
            <Button.Or />
            <Button
              positive
              type="submit"
              loading={isSubmitting}
              disabled={!isValid || isSubmitting}
              data-testid="placeOrderButton"
            >
              Place Order
            </Button>
          </Button.Group>
        </Form>
      )}
    </Panel>
  );
}
